import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchUsers,
  fetchPermits,
  fetchRoles,
  createUser,
  updateUser,
  deleteUser,
  resetPassword,
} from "./services";
import { useAuth0 } from "@auth0/auth0-react";
import { useApp } from "../../AppProvider";
import { MESSAGES, QUERY_OPTIONS_CACHE } from "./constants";

export function useUsersManagement() {
  const { getAccessTokenSilently } = useAuth0();
  const { doToast } = useApp();
  const queryClient = useQueryClient();

  // Queries
  const usersQuery = useQuery(
    ["users"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchUsers(token);
    },
    {
      ...QUERY_OPTIONS_CACHE,
      // Ensure the list is refreshed when navigating back to the page,
      // but don't refetch on focus/reconnect (which can briefly omit a just-created user).
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const permitsQuery = useQuery(
    ["permits"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchPermits(token);
    },
    { ...QUERY_OPTIONS_CACHE, refetchOnMount: "always" }
  );

  const rolesQuery = useQuery(
    ["roles"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchRoles(token);
    },
    QUERY_OPTIONS_CACHE
  );

  // Mutations
  const createOrUpdateMutation = useMutation(
    async ({ formData, isCreateMode }) => {
      const token = await getAccessTokenSilently();
      if (isCreateMode) {
        return createUser({ token, payload: formData });
      } else {
        return updateUser({
          token,
          userId: formData.auth0_user_id,
          payload: { ...formData, auth0_user_id: undefined },
        });
      }
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(["users"]);
      },
      onSuccess: (data, { formData, isCreateMode }) => {
        queryClient.setQueryData(["users"], (old = []) => {
          const updatedUser = {
            ...formData,
            auth0_user_id: data.auth0_user_id || formData.auth0_user_id,
          };
          if (!updatedUser.auth0_user_id) {
            // If the API didn't return an id (shouldn't happen), don't corrupt the grid's row ids.
            return old;
          }

          if (isCreateMode) {
            const dedupedOld = old.filter(
              (u) => u.auth0_user_id !== updatedUser.auth0_user_id
            );
            return [updatedUser, ...dedupedOld];
          }

          return old.map((u) =>
            u.auth0_user_id === updatedUser.auth0_user_id ? updatedUser : u
          );
        });
        doToast("success", data?.message || MESSAGES.SUCCESS_CREATE);
      },
      onError: (error) => {
        console.error("Create/Update error:", error);
        const msg =
          error?.response?.data?.error ||
          error.message ||
          MESSAGES.ERROR_CREATE;
        doToast("error", `Error creating/updating user: ${msg}`);
      },
    }
  );

  const deleteMutation = useMutation(
    async (userId) => {
      const token = await getAccessTokenSilently();
      return deleteUser({ token, userId });
    },
    {
      onSuccess: (data, userId) => {
        queryClient.setQueryData(["users"], (old = []) =>
          old.filter((user) => user.auth0_user_id !== userId)
        );
        doToast("success", data?.message || MESSAGES.SUCCESS_DELETE);
      },
      onError: (error) => {
        console.error("Delete error:", error);
        const msg =
          error?.response?.data?.error ||
          error.message ||
          MESSAGES.ERROR_DELETE;
        doToast("error", `Error deleting user: ${msg}`);
      },
    }
  );

  const resetPasswordMutation = useMutation(
    async (userId) => {
      const token = await getAccessTokenSilently();
      return resetPassword({ token, userId });
    },
    {
      onSuccess: (data) => {
        doToast("success", data?.message || "Password reset email sent.");
      },
      onError: (error) => {
        console.error("Reset password error:", error);
        const msg =
          error?.response?.data?.error ||
          error.message ||
          "Failed to reset password.";
        doToast("error", `Error resetting password: ${msg}`);
      },
    }
  );

  return {
    usersQuery,
    permitsQuery,
    rolesQuery,
    createOrUpdateMutation,
    deleteMutation,
    resetPasswordMutation,
  };
}
